import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import useMediaQuery from '@mui/material/useMediaQuery';

import { duplicateDoc, tableComponent } from '../constants/appConstants';
import { useToast } from '../toast/toastContext';
import {
  getDocumentDetails,
  processDuplicateDocument,
  getSelectedDocs,
  deleteDocument
} from '../services/services';
import '../image.css';
import { get_queue } from '../redux/actions/action';
import Prompt from './prompt';
import ReactGA from 'react-ga4';
import {
  defaultFilter,
  formatDate,
  getStatusKey,
  buildFilteredDocPayload
} from '../services/common';
import { isEmpty } from 'lodash';
import { InfoOutlined } from '@mui/icons-material';
import EmailVendor from './emailVendor';
import { getStyles } from '../styles/duplicateDoc.styles';
import { useUserRole } from '../hooks/useIdentity';
import { clearSessionPreservingTab } from '../utils/tabSession';

export default function DuplicateDoc({ handleClose, id, documentName, setOpened }) {
  const styles = getStyles();
  const smallScreen = useMediaQuery('(max-height:1500px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState({});
  const { PREVIOUS, NEW, DUPLICATE_DOC, CONTINUE, DELETE, DOCUMENT_IMAGE_NOT_FOUND } = duplicateDoc;
  const { DELETE_DOCUMENT_MESSAGE, DELETE_DOCUMENT } = tableComponent;
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const currentFilters = useSelector((state) => state?.filters?.filters);
  const savedFilters = !isEmpty(currentFilters) ? currentFilters : defaultFilter;
  const globalActiveTab = useSelector((state) => state?.tabs?.tabs);
  const tableFilters = useSelector((state) => state?.table?.tableFilters);

  const userRole = useUserRole();

  const googleEvent = (action) => {
    ReactGA.event({
      category: 'Document Management',
      action: action,
      label: `Proceeded Document: ${id} `,
      value: 1
    });
  };
  const callSource = userRole === 'Vendor' ? 'api' : 'app';
  const handleContinue = async () => {
    const pageSize = tableFilters?.dataGridPagination?.pageSize;

    setProcessing(true);
    const res = await processDuplicateDocument(id);
    if (res?.status === 200) {
      showToast(res?.data?.metadata?.message, 'success');
      handleClose();
      const response = await getSelectedDocs(
        '',
        buildFilteredDocPayload(tableFilters, savedFilters),
        callSource,
        false,
        1,
        getStatusKey(globalActiveTab?.documentChildActiveTab),
        pageSize
      );
      if (response?.response?.status === 401) {
        clearSessionPreservingTab();
        navigate('/');
      }
      dispatch(get_queue(response?.data?.payload?.data));
    } else {
      showToast(res?.response?.data?.metadata?.message || res?.data?.metadata?.message, 'error');
    }
    googleEvent('Proceed Anyway');
    setProcessing(false);
  };
  const selectedData = async () => {
    const result = await getDocumentDetails(id, callSource);
    if (result?.response?.status === 401) {
      clearSessionPreservingTab();
      navigate('/');
    }
    setData(result?.data?.payload?.data);
    setLoading(false);
  };
  useEffect(() => {
    selectedData();
  }, []);
  const deleteDocs = async (id) => {
    const pageSize = tableFilters?.dataGridPagination?.pageSize;

    setDeleting(true);
    const params = {
      documentIds: [id]
    };
    const result = await deleteDocument(params);
    if (result?.status === 200) {
      const response = await getSelectedDocs(
        '',
        buildFilteredDocPayload(tableFilters, savedFilters),
        callSource,
        false,
        1,
        getStatusKey(globalActiveTab?.documentChildActiveTab),
        pageSize
      );
      setDeleting(false);
      showToast(result?.data?.metadata?.message, 'success');
      setOpen(false);
      setOpened(false);
      if (response?.response?.status === 401) {
        clearSessionPreservingTab();
        navigate('/');
      }
      dispatch(get_queue(response?.data?.payload?.data));
    } else {
      showToast(
        result?.response?.data?.metadata?.message || result?.data?.metadata?.message,
        'error'
      );
    }
  };

  const handleDeleteDocument = (e) => {
    e?.stopPropagation?.();
    googleEvent('Delete Document');
    deleteDocs(id);
  };
  const [isEnlargedArrayPrev, setIsEnlargedArrayPrev] = useState(
    Array(data?.previousDocImageUrl?.length).fill(false)
  );
  const [isEnlargedArrayNew, setIsEnlargedArrayNew] = useState(
    Array(data?.newDocImageUrl?.length).fill(false)
  );
  const handleImageClickPrev = (index) => {
    const updatedEnlargedArrayPrev = [...isEnlargedArrayPrev];
    updatedEnlargedArrayPrev[index] = !updatedEnlargedArrayPrev[index];
    setIsEnlargedArrayPrev(updatedEnlargedArrayPrev);
  };

  const handleImageClickNew = (index) => {
    const updatedEnlargedArrayNew = [...isEnlargedArrayNew];
    updatedEnlargedArrayNew[index] = !updatedEnlargedArrayNew[index];
    setIsEnlargedArrayNew(updatedEnlargedArrayNew);
  };

  return (
    <Grid>
      <div style={styles.title}>{DUPLICATE_DOC}</div>
      <>
        <Grid sx={styles.dashedBorderContainer}>
          <Grid sx={styles.innerContent(loading)}>
            {loading ? (
              <Grid container sx={styles.loaderContainer}>
                <CircularProgress size={50} color="primary" />
              </Grid>
            ) : (
              <Grid sx={styles.mainContentGrid} container>
                <Grid container item xs={12} sx={styles.topRowGrid}>
                  <Grid xs={5}>
                    {data?.previousDocImageFileName?.map((prevDocName) => {
                      return (
                        <Typography sx={styles.fileNameTypography}>{prevDocName}</Typography>
                      );
                    })}
                    <Box sx={styles.datePillWrapper}>
                      <Box sx={styles.datePill}>{formatDate(data?.previousDocCreatedAt)}</Box>
                    </Box>
                  </Grid>
                  <Grid xs={5}>
                    {data?.newDocImageFileName?.map((newDocName) => {
                      return <Typography sx={styles.fileNameTypography}>{newDocName}</Typography>;
                    })}
                    <Box sx={styles.datePillWrapper}>
                      <Box sx={styles.datePill}>{formatDate(data?.newDocCreatedAt)}</Box>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid xs={5}>
                    {isEmpty(data?.previousDocImageUrl) || data?.previousDocImageUrl === null ? (
                      <Grid item sx={styles.errorContainer}>
                        <Typography sx={styles.errorText}>
                          <InfoOutlined sx={styles.errorIcon} />
                          {DOCUMENT_IMAGE_NOT_FOUND}
                        </Typography>
                      </Grid>
                    ) : (
                      data?.previousDocImageUrl?.map((prevImg, prevIndex) => {
                        return (
                          <Grid
                            key={`prevImg_${prevIndex}`}
                            className={`duplicateImage-container ${
                              isEnlargedArrayPrev[prevIndex] ? 'duplicateEnlarged' : ''
                            }`}
                            onClick={() => handleImageClickPrev(prevIndex)}>
                            <img
                              id="i"
                              style={styles.documentImage(
                                isEnlargedArrayPrev[prevIndex],
                                smallScreen
                              )}
                              src={prevImg}
                              alt="new"
                            />
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                  <Grid xs={2} sx={styles.compareArrowsContainer}>
                    <CompareArrowsIcon sx={styles.compareArrowsIcon} />
                  </Grid>
                  <Grid xs={5}>
                    {data?.newDocImageUrl?.map((newImg, newIndex) => {
                      return (
                        <Grid
                          key={`newImg_${newIndex}`}
                          className={`duplicateImage-container ${
                            isEnlargedArrayNew[newIndex] ? 'duplicateEnlarged' : ''
                          }`}
                          onClick={() => handleImageClickNew(newIndex)}>
                          <img
                            id="i"
                            style={styles.documentImage(isEnlargedArrayNew[newIndex], smallScreen)}
                            src={newImg}
                            alt="new"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </>

      <Grid container xs={12} sx={styles.buttonsRow}>
        <Button
          onClick={handleContinue}
          disabled={loading || processing}
          variant="contained"
          sx={styles.submitButton}>
          {processing ? <CircularProgress size={25} color="inherit" /> : CONTINUE}
        </Button>
        <Box sx={styles.emailButtonWrapper}>
          <EmailVendor
            button="deleteAndSendEmail"
            documentName={documentName}
            selectedData={handleDeleteDocument}
            setOpened={setOpened}
            id={id}
          />
        </Box>
        <Button variant="contained" onClick={() => setOpen(true)} sx={styles.deleteButton}>
          {DELETE}
        </Button>
        <Prompt
          title={DELETE_DOCUMENT}
          description={DELETE_DOCUMENT_MESSAGE}
          leftButton="Cancel"
          rightButton="Delete"
          deleteContent={documentName}
          setOpen={setOpen}
          open={open}
          opacity={true}
          handleClick={handleDeleteDocument}
          loading={deleting}
        />
      </Grid>
    </Grid>
  );
}
